import type { Edge, Node } from '@xyflow/react'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'
import { shouldShowNoInputWarning } from '@/features/backtest/utils/universal-node-validation'
import { getParameterValue } from '@/features/backtest/components/nodes/universal-node-helpers'
import type { NodeData } from '@/features/backtest/types/nodes'
import { isInputRequiredNow } from '@/features/backtest/schemas/input-activity'
import { nodeName } from '@/features/backtest/utils/node-display-name'

export interface NodeValidationError {
  nodeId: string
  nodeLabel: string
  missingInputs: string[]
}

/**
 * Validate that every node has its required input connections before a backtest
 * run. Several strategy nodes only require an input under a specific parameter
 * value (e.g. a Stop needs a connection only for "Static Value"); those are
 * gated below so a default configuration that needs NO connection isn't flagged.
 * The per-node gating MUST stay in sync with how `UniversalNode` renders the
 * input handle — if the UI hides the handle for a given parameter, requiring a
 * connection here would be impossible to satisfy.
 */
export function validateGraphBeforeBacktest(
  nodes: Node<NodeData>[],
  edges: Edge[],
): NodeValidationError[] {
  const errors: NodeValidationError[] = []

  for (const node of nodes) {
    const nodeType = node.type || (node.data as NodeData)?.type
    if (!nodeType || nodeType === 'subnet') continue

    const schema = UniversalNodeRegistry.getSchema(nodeType as string)
    if (!schema) continue

    const inputs = schema.inputs || []

    // Get all edges targeting this node
    const incomingEdges = edges.filter((e) => e.target === node.id)
    const connectedHandles = new Set(incomingEdges.map((e) => e.targetHandle))

    // Dynamic-input gates (And/Or Gate, Switcher) declare `inputs: []` and
    // generate input_1/input_2/... at wire time, so the per-input loop below
    // can't see their requirement — it lives in `inputHandler.minInputs`.
    // Without this check, an under-filled gate (e.g. an And/Or Gate wired with
    // only 1 of its >= 2 inputs) passed the run gate and was silently dropped by
    // the codegen, so the user got a backtest result with the gate doing nothing
    // — "no error, but the gate (and anything it was meant to combine) has no
    // effect" (bt_d68087a21988). Flag it so the user gets an actionable error.
    if (inputs.length === 0) {
      const ih = schema.inputHandler
      if (ih?.type === 'multi') {
        const minInputs = ih.minInputs ?? 2
        if (connectedHandles.size < minInputs) {
          errors.push({
            nodeId: node.id,
            nodeLabel: nodeName(node),
            missingInputs: [
              `at least ${minInputs} connected inputs (has ${connectedHandles.size})`,
            ],
          })
        }
      }
      continue
    }

    // Check required inputs
    const missingInputs: string[] = []

    // Either stored shape (record or descriptor array): isInputRequiredNow and
    // getParameterValue both read it.
    const params = (node.data as NodeData)?.parameters

    // Check each input using the shared shouldShowNoInputWarning helper
    for (let i = 0; i < inputs.length; i++) {
      const inputDef = inputs[i]
      // Inputs the node's settings do not use (a % Trade stop's signal, a
      // Market entry's price) need no wire: the rule the node face and the
      // readiness validator read too (schemas/input-activity.ts).
      if (!isInputRequiredNow(schema, inputDef, params)) continue
      const isConnected = connectedHandles.has(inputDef.id)

      // Check for static mode - input has discriminatedUnion and parameter is set to 'static'
      let isStaticMode = false
      if (inputDef.discriminatedUnion) {
        const paramName = inputDef.discriminatedUnion.parameterName
        isStaticMode = getParameterValue(params, paramName) === 'static'
      }

      // Use the shared helper - same logic as UniversalNode.tsx uses for "No input" display
      if (shouldShowNoInputWarning(schema, i, isConnected, isStaticMode)) {
        missingInputs.push(inputDef.displayName)
      }
    }

    if (missingInputs.length > 0) {
      errors.push({
        nodeId: node.id,
        nodeLabel: nodeName(node),
        missingInputs,
      })
    }
  }

  return errors
}
